import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  InputGroup,
} from "react-bootstrap";
import moment from "moment";

import {
  getRemitosPendientesProveedor,
  getRemitoById,
} from "../helpers/rutaRemitos";
import {
  postOrdenPagoRemito,
  editarOrdenPagoRemito,
  getOrdenPagoRemitoById,
} from "../helpers/rutaOrdenPagoRemitos";
import { getSaldosTesoreria } from "../helpers/rutaTesoreria";
import { generarPDFPendientesProveedor } from "../helpers/pdfPendientesProveedor";
import {
  getCartera,
  getCarteraDisponibles,
} from "../helpers/rutaCarteras";
import { getAjustesCtaCtePendientes } from "../helpers/rutaAjustesCtaCte";

const moneyInput = (value) => {
  if (value === "" || value == null) return "";

  return Number(value || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseMoneyInput = (value) => {
  const raw = String(value ?? "")
    .replace(/[^\d.,-]/g, "")
    .trim();

  if (!raw) return 0;

  if (raw.includes(",")) {
    return Number(
      raw.replace(/\./g, "").replace(",", ".")
    );
  }

  return Number(raw.replace(/,/g, ""));
};

const limpiarTextoRetencion = (value) => {
  let texto = String(value ?? "")
    .replace(/[^\d.,-]/g, "")
    .trim();

  if (!texto) return "";

  const negativo = texto.startsWith("-");
  texto = texto.replace(/-/g, "");

  const lastComma = texto.lastIndexOf(",");
  const lastDot = texto.lastIndexOf(".");
  const sepIndex = Math.max(lastComma, lastDot);

  let entero =
    sepIndex >= 0 ? texto.slice(0, sepIndex) : texto;

  const decimal =
    sepIndex >= 0 ? texto.slice(sepIndex) : "";

  entero = entero.replace(/^0+(?=\d)/, "");

  if (!entero && decimal) entero = "0";

  return `${negativo ? "-" : ""}${entero}${decimal}`;
};

const parseRetencionInput = (value) => {
  const raw = limpiarTextoRetencion(value);

  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");
  const sepIndex = Math.max(lastComma, lastDot);

  if (sepIndex >= 0) {
    const entero = raw
      .slice(0, sepIndex)
      .replace(/[.,]/g, "");

    const decimal = raw
      .slice(sepIndex + 1)
      .replace(/[^\d]/g, "");

    return Number(`${entero || 0}.${decimal || 0}`);
  }

  return Number(raw.replace(/[^\d-]/g, ""));
};

const $ = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const IVA_105 = 1.105;
const LOGO_SAN_PANCRACIO = "/SanPancracio.png";

const fmtFechaDoc = (d) =>
  d ? moment.utc(d).format("DD/MM/YYYY") : "-";

function calcularTotalesRemito(rem) {
  let totalA = Number(rem.totalCompraA || 0);
  let totalB = Number(rem.totalCompraB || 0);

  const kg = Number(rem.kgRecibido || 0);
  const pc = Number(rem.precioCompra || 0);

  const base =
    rem.totalCompra != null
      ? Number(rem.totalCompra || 0)
      : kg * pc;

  if (totalA <= 0.01 && totalB <= 0.01 && base > 0) {
    const porcB = Number(rem.porcentajeCompra || 0) / 100;

    const compraNegro = base * porcB;
    const compraBlancoBase = base - compraNegro;
    const compraBlancoIVA = compraBlancoBase * IVA_105;

    totalA = compraBlancoIVA;
    totalB = compraNegro;
  }

  return { totalA, totalB };
}

function calcularSaldoAjuste(aj) {
  const saldoA = Number(aj?.saldoA || 0);
  const saldoB = Number(aj?.saldoB || 0);

  return Number((saldoA + saldoB).toFixed(2));
}

function distribuirPagoPorCuenta(
  documentos,
  montos,
  esAjuste = false
) {
  const cents = (v) =>
    Math.max(0, Math.round(Number(v || 0) * 100));

  const restantes = {
    A: cents(montos.A),
    B: cents(montos.B),
  };

  const orden = [...documentos].sort(
    (a, b) =>
      new Date(a.fecha || 0) - new Date(b.fecha || 0) ||
      Number(a.codrem || 0) - Number(b.codrem || 0) ||
      String(a._id).localeCompare(String(b._id))
  );

  const apps = [];

  for (const doc of orden) {
    const totales = esAjuste
      ? null
      : calcularTotalesRemito(doc);

    const pendienteA = esAjuste
      ? cents(doc.saldoA)
      : cents(
          doc.pendienteA ??
            (cents(totales.totalA) - cents(doc.pagadoA)) / 100
        );

    const pendienteB = esAjuste
      ? cents(doc.saldoB)
      : cents(
          doc.pendienteB ??
            (cents(totales.totalB) - cents(doc.pagadoB)) / 100
        );

    const A = Math.min(pendienteA, restantes.A);
    const B = Math.min(pendienteB, restantes.B);

    if (A + B <= 0) continue;

    apps.push({
      [esAjuste ? "ajuste" : "remito"]: doc._id,
      aplicado: (A + B) / 100,
      aplicadoA: A / 100,
      aplicadoB: B / 100,
      aplicadoFlete: 0,
    });

    restantes.A -= A;
    restantes.B -= B;
  }

  return {
    apps,
    restantes: {
      A: restantes.A / 100,
      B: restantes.B / 100,
    },
    restante: (restantes.A + restantes.B) / 100,
  };
}

function leerImporteCuenta(value) {
  const texto = String(value ?? "").trim();

  if (!texto) return 0;

  if (
    /^\d+(,\d{0,2})?$/.test(texto) ||
    /^\d{1,3}(\.\d{3})+(,\d{0,2})?$/.test(texto)
  ) {
    return Number(
      texto.replace(/\./g, "").replace(",", ".")
    );
  }

  return NaN;
}

export default function ModalOrdenPagoRemitos({
  show,
  onHide,
  proveedor,
  cliente = null,
  preseleccion = [],
  opId = null,
}) {
  const esEdicion = Boolean(opId);

  const [opOriginal, setOpOriginal] = useState(null);

  const proveedorActivo =
    esEdicion ? opOriginal?.proveedor : proveedor;

  const clienteActivo =
    esEdicion ? opOriginal?.cliente : cliente;

  const [fecha, setFecha] = useState(
    moment().format("YYYY-MM-DD")
  );

  const [obs, setObs] = useState("");

  const [pendientes, setPendientes] = useState([]);
  const [seleccion, setSeleccion] = useState([]);

  const [ajustesPendientes, setAjustesPendientes] = useState([]);
  const [seleccionAjustes, setSeleccionAjustes] = useState([]);

  const [filtrarCliente, setFiltrarCliente] = useState(false);

  const [pagaA, setPagaA] = useState(false);
  const [pagaB, setPagaB] = useState(false);

  const [importesCuenta, setImportesCuenta] = useState({
    A: "",
    B: "",
  });

  const [medios, setMedios] = useState([]);
  const [rets, setRets] = useState([]);

  const [importeEditando, setImporteEditando] = useState(null);
  const [importeTexto, setImporteTexto] = useState({});

  const [retEditando, setRetEditando] = useState(null);
  const [retTexto, setRetTexto] = useState({});

  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);

  const [carteraCheques, setCarteraCheques] = useState([]);
  const [carteraICheqs, setCarteraICheqs] = useState([]);

 // ------------------------------------------------------------
  // CARGAR OP EXISTENTE CUANDO ESTAMOS EDITANDO
  // ------------------------------------------------------------
  useEffect(() => {
    if (!show) return;

    if (!esEdicion) {
      setOpOriginal(null);
      return;
    }

    (async () => {
      try {
        const r = await getOrdenPagoRemitoById(opId);

        if (!r?.ok || !r?.orden) {
          alert(
            r?.msg ||
              "No se pudo cargar la Orden de Pago."
          );
          return;
        }

        const op = r.orden;

        if (
          String(op.estado || "").toLowerCase() ===
          "anulada"
        ) {
          alert(
            "No se puede editar una Orden de Pago anulada."
          );

          onHide(false);
          return;
        }

        setOpOriginal(op);

        // Fecha
        setFecha(
          op.fecha
            ? moment
                .utc(op.fecha)
                .format("YYYY-MM-DD")
            : moment().format("YYYY-MM-DD")
        );

        // Observaciones
        setObs(op.observaciones || "");

        // Si la OP tenía cliente, conservamos ese filtro.
        setFiltrarCliente(Boolean(op.cliente));

        // ------------------------------------------------------
        // RECONSTRUIR IMPORTES ORIGINALES A / B
        // ------------------------------------------------------

        const apps = op.aplicaciones || [];
        const appsAj =
          op.aplicacionesAjustes || [];

        const importeA = Number(
          (
            [...apps, ...appsAj].reduce(
              (s, ap) =>
                s +
                Number(
                  ap?.aplicadoA || 0
                ),
              0
            ) +
            Number(
              op?.saldoFavor?.generadoA || 0
            )
          ).toFixed(2)
        );

        const importeB = Number(
          (
            [...apps, ...appsAj].reduce(
              (s, ap) =>
                s +
                Number(
                  ap?.aplicadoB || 0
                ),
              0
            ) +
            Number(
              op?.saldoFavor?.generadoB || 0
            )
          ).toFixed(2)
        );

        setPagaA(importeA > 0);
        setPagaB(importeB > 0);

        setImportesCuenta({
          A:
            importeA > 0
              ? moneyInput(importeA)
              : "",
          B:
            importeB > 0
              ? moneyInput(importeB)
              : "",
        });

        // ------------------------------------------------------
        // MEDIOS DE PAGO ORIGINALES
        // ------------------------------------------------------

        setMedios(
          (op.pagos || []).map((m) => ({
            medio: String(
              m?.medio || ""
            ).toLowerCase(),

            importe: Number(
              m?.importe || 0
            ),

            referencia:
              m?.referencia || "",

            caja:
              m?.caja?._id ||
              m?.caja ||
              "",

            cuentaBancaria:
              m?.cuentaBancaria?._id ||
              m?.cuentaBancaria ||
              "",

            carteraId:
              m?.carteraId?._id ||
              m?.carteraId ||
              "",
          }))
        );

        // ------------------------------------------------------
        // RETENCIONES ORIGINALES
        // ------------------------------------------------------

        setRets(
          (op.retenciones || []).map((r) => ({
            tipo:
              r?.tipo || "IIBB",

            base: Number(
              r?.base || 0
            ),

            porcentaje: Number(
              r?.porcentaje || 0
            ),

            importe: Number(
              r?.importe || 0
            ),

            comprobante:
              r?.comprobante || "",
          }))
        );

        // Limpiamos cualquier edición visual anterior.
        setImporteEditando(null);
        setImporteTexto({});
        setRetEditando(null);
        setRetTexto({});
      } catch (e) {
        console.error(
          "Error cargando OP para editar:",
          e?.response?.data || e
        );

        alert(
          e?.response?.data?.msg ||
            e.message ||
            "Error cargando la Orden de Pago."
        );
      }
    })();
  }, [
    show,
    esEdicion,
    opId,
    onHide,
  ]); 

  const totalesPendientes = pendientes.reduce(
    (acc, r) => {
      const pendA = Number(
        r.pendienteA ??
          Math.max(
            0,
            Number(r.totalCompraA || 0) -
              Number(r.pagadoA || 0)
          )
      );

      const pendB = Number(
        r.pendienteB ??
          Math.max(
            0,
            Number(r.totalCompraB || 0) -
              Number(r.pagadoB || 0)
          )
      );

      const saldo = Number((pendA + pendB).toFixed(2));

      acc.compraA += pendA;
      acc.compraB += pendB;
      acc.saldo += saldo;

      return acc;
    },
    {
      compraA: 0,
      compraB: 0,
      flete: 0,
      saldo: 0,
    }
  );

  const totalSeleccion = useMemo(
    () =>
      seleccion.reduce(
        (a, r) => a + Number(r.saldo || 0),
        0
      ),
    [seleccion]
  );

  const totalSeleccionAjustes = useMemo(
    () =>
      seleccionAjustes.reduce(
        (a, aj) => a + calcularSaldoAjuste(aj),
        0
      ),
    [seleccionAjustes]
  );

  const totalSeleccionGeneral = useMemo(
    () =>
      Number(
        (
          Number(totalSeleccion) +
          Number(totalSeleccionAjustes)
        ).toFixed(2)
      ),
    [totalSeleccion, totalSeleccionAjustes]
  );

  const totalPagos = useMemo(
    () =>
      medios.reduce(
        (a, m) => a + Number(m.importe || 0),
        0
      ),
    [medios]
  );

  const totalRets = useMemo(
    () =>
      rets.reduce(
        (a, r) => a + Number(r.importe || 0),
        0
      ),
    [rets]
  );

  const montoAplicable = useMemo(
    () =>
      Number(
        (Number(totalPagos) + Number(totalRets)).toFixed(2)
      ),
    [totalPagos, totalRets]
  );

  const hayConceptosPago = pagaA || pagaB;

  const cuentasActivas = [
    pagaA && "A",
    pagaB && "B",
  ].filter(Boolean);

  const montosPorCuenta = useMemo(() => {
    const unica = Number(pagaA) + Number(pagaB) === 1;

    return {
      A: pagaA
        ? unica
          ? montoAplicable
          : leerImporteCuenta(importesCuenta.A)
        : 0,

      B: pagaB
        ? unica
          ? montoAplicable
          : leerImporteCuenta(importesCuenta.B)
        : 0,

      Flete: 0,
    };
  }, [pagaA, pagaB, montoAplicable, importesCuenta]);

  const repartoValido =
    hayConceptosPago &&
    [montosPorCuenta.A, montosPorCuenta.B].every(
      (n) =>
        Number.isFinite(n) &&
        n >= 0 &&
        Number.isSafeInteger(Math.round(n * 100))
    ) &&
    Math.round(montosPorCuenta.A * 100) +
      Math.round(montosPorCuenta.B * 100) ===
      Math.round(montoAplicable * 100);

  const aplicacionesDistribuidas = useMemo(
    () =>
      distribuirPagoPorCuenta(
        seleccion,
        repartoValido
          ? montosPorCuenta
          : { A: 0, B: 0 }
      ),
    [seleccion, montosPorCuenta, repartoValido]
  );

  const aplicacionesAjustesDistribuidas = useMemo(
    () =>
      distribuirPagoPorCuenta(
        seleccionAjustes,
        aplicacionesDistribuidas.restantes,
        true
      ),
    [seleccionAjustes, aplicacionesDistribuidas]
  );

  const saldoFavorGenerado =
    aplicacionesAjustesDistribuidas.restante;

  const diferenciaVisual = useMemo(
    () =>
      (totalSeleccionGeneral - montoAplicable).toFixed(2),
    [totalSeleccionGeneral, montoAplicable]
  );

  const handlePdfPendientes = () => {
    generarPDFPendientesProveedor({
      proveedor: proveedorActivo,
      cliente: filtrarCliente
        ? clienteActivo
        : null,
      pendientes,
      totales: totalesPendientes,
      fecha,
      logoUrl: LOGO_SAN_PANCRACIO,
      empresa: {
        nombre: "SAN PANCRACIO SRL",
        direccion:
          "Avda. Peron 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán",
        telefono: "Tel: 0381-3008829",
        email: "contacto@sanpancracio.com",
      },
    }).catch((err) => {
      console.error("Error generando PDF de pendientes:", err);
      alert("No se pudo generar el PDF de pendientes.");
    });
  };

useEffect(() => {
  const proveedorId =
    proveedorActivo?._id || proveedorActivo;

  const clienteId =
    clienteActivo?._id || clienteActivo;

  if (!show || !proveedorId) return;

  (async () => {
    const basePendientes =
      await getRemitosPendientesProveedor(
        proveedorId,
        filtrarCliente
          ? clienteId
          : undefined
      );

    /*
      En edición puede ocurrir que un remito usado por
      esta misma OP haya quedado totalmente pagado.

      En ese caso ya no viene dentro de "pendientes",
      pero necesitamos volver a mostrarlo para poder
      editar correctamente la orden.
    */
    const mapBase = new Map();

    (basePendientes || []).forEach((r) => {
      mapBase.set(
        String(r._id),
        r
      );
    });

    if (esEdicion && opOriginal) {
      (opOriginal.aplicaciones || []).forEach((ap) => {
        const rem = ap?.remito;

        const id =
          rem?._id ||
          rem;

        if (!id) return;

        if (!mapBase.has(String(id))) {
          mapBase.set(
            String(id),
            typeof rem === "object"
              ? rem
              : { _id: id }
          );
        }
      });
    }

    const baseList =
      [...mapBase.values()];

    const list = await Promise.all(
      baseList.map(async (r) => {
        try {
          const resp =
            await getRemitoById(r._id);

          const rem =
            resp?.remito || resp || {};

          /*
            Si este remito ya estaba aplicado en la OP,
            sumamos nuevamente ese importe al pendiente
            visual.

            El backend hará la reversa real dentro de
            la transacción recién al guardar.
          */
          const appOriginal =
            esEdicion && opOriginal
              ? (
                  opOriginal.aplicaciones || []
                ).find((ap) => {
                  const id =
                    ap?.remito?._id ||
                    ap?.remito;

                  return (
                    String(id) ===
                    String(r._id)
                  );
                })
              : null;

          const { totalA, totalB } =
            calcularTotalesRemito(rem);

          const pagadoA =
            Number(rem.pagadoA || 0);

          const pagadoB =
            Number(rem.pagadoB || 0);

          const pagadoProv =
            Number(
              rem.pagadoProveedor || 0
            );

          const pendienteA = Math.max(
            0,
            totalA -
              pagadoA +
              Number(
                appOriginal?.aplicadoA || 0
              )
          );

          const pendienteB = Math.max(
            0,
            totalB -
              pagadoB +
              Number(
                appOriginal?.aplicadoB || 0
              )
          );

          const saldoPendiente =
            Number(
              (
                pendienteA +
                pendienteB
              ).toFixed(2)
            );

          return {
            ...r,

            totalCompraA: totalA,
            totalCompraB: totalB,

            flete: 0,

            pagadoProveedor:
              pagadoProv,

            pagadoA,
            pagadoB,

            pagadoFlete: 0,

            pendienteA,
            pendienteB,

            pendienteFlete: 0,

            saldoPendiente,
          };
        } catch (err) {
          console.error(
            "Error cargando detalle remito",
            r._id,
            err
          );

          return {
            ...r,

            totalCompraA: 0,
            totalCompraB: 0,

            flete: 0,

            pagadoProveedor: 0,
            pagadoA: 0,
            pagadoB: 0,
            pagadoFlete: 0,

            pendienteA: 0,
            pendienteB: 0,
            pendienteFlete: 0,

            saldoPendiente: 0,
          };
        }
      })
    );

    const ordenadas =
      (list || [])
        .slice()
        .sort(
          (a, b) =>
            new Date(a.fecha || 0) -
            new Date(b.fecha || 0)
        );

    const map = new Map(
      ordenadas.map((r) => [
        String(r._id),
        r,
      ])
    );

    const sel = [];

    if (esEdicion && opOriginal) {
      /*
        Al abrir Editar, dejamos seleccionados
        exactamente los remitos que tenía la OP.
      */
      const idsOriginales = new Set(
        (
          opOriginal.aplicaciones || []
        ).map((ap) =>
          String(
            ap?.remito?._id ||
              ap?.remito
          )
        )
      );

      ordenadas.forEach((r) => {
        if (
          !idsOriginales.has(
            String(r._id)
          )
        ) {
          return;
        }

        const saldo = Number(
          (
            Number(r.pendienteA || 0) +
            Number(r.pendienteB || 0)
          ).toFixed(2)
        );

        if (saldo > 0.01) {
          sel.push({
            ...r,
            saldo,
          });
        }
      });
    } else {
      /*
        Alta normal: conservamos exactamente
        el comportamiento que ya teníamos.
      */
      const seed = [
        ...preseleccion,

        ...seleccion.filter((x) =>
          map.has(String(x._id))
        ),
      ];

      seed.forEach((s) => {
        const r =
          map.get(String(s._id));

        if (!r) return;

        const pendA =
          Number(
            r.pendienteA ?? 0
          );

        const pendB =
          Number(
            r.pendienteB ?? 0
          );

        const saldo = Number(
          (
            pendA +
            pendB
          ).toFixed(2)
        );

        if (saldo > 0.01) {
          sel.push({
            ...r,
            saldo,
          });
        }
      });
    }

    setPendientes(ordenadas);
    setSeleccion(sel);

    try {
      const resAj = await getAjustesCtaCtePendientes({
        entidadTipo: "PROVEEDOR",
        proveedor: proveedorId,
        movimiento: "A_PAGAR",
      });

      const listaAj = Array.isArray(resAj)
        ? resAj
        : Array.isArray(resAj?.ajustes)
        ? resAj.ajustes
        : Array.isArray(resAj?.data)
        ? resAj.data
        : [];

      const mapAjustes = new Map();

      listaAj.forEach((aj) => {
        mapAjustes.set(
          String(aj._id),
          aj
        );
      });

      /*
        En edición reincorporamos los ajustes
        que pertenecían a esta misma OP, aunque
        actualmente hayan quedado en saldo cero.
      */
      if (esEdicion && opOriginal) {
        (
          opOriginal.aplicacionesAjustes || []
        ).forEach((ap) => {
          const ajuste = ap?.ajuste;

          const id =
            ajuste?._id ||
            ajuste;

          if (!id) return;

          if (!mapAjustes.has(String(id))) {
            mapAjustes.set(
              String(id),
              typeof ajuste === "object"
                ? ajuste
                : { _id: id }
            );
          }
        });
      }

      const ordenAj =
        [...mapAjustes.values()]
          .map((aj) => {
            const appOriginal =
              esEdicion && opOriginal
                ? (
                    opOriginal.aplicacionesAjustes || []
                  ).find((ap) => {
                    const id =
                      ap?.ajuste?._id ||
                      ap?.ajuste;

                    return (
                      String(id) ===
                      String(aj._id)
                    );
                  })
                : null;

            /*
              Igual que con remitos:
              devolvemos visualmente lo que esta OP
              había aplicado, sin tocar todavía la DB.
            */
            const saldoA =
              Number(aj.saldoA || 0) +
              Number(
                appOriginal?.aplicadoA || 0
              );

            const saldoB =
              Number(aj.saldoB || 0) +
              Number(
                appOriginal?.aplicadoB || 0
              );

            const saldo = Number(
              (
                saldoA +
                saldoB
              ).toFixed(2)
            );

            return {
              ...aj,
              saldoA,
              saldoB,
              saldoFlete: 0,
              saldoTotal: saldo,
              saldo,
            };
          })
          .filter(
            (aj) =>
              calcularSaldoAjuste(aj) > 0.01
          )
          .sort(
            (a, b) =>
              new Date(a.fecha || 0) -
              new Date(b.fecha || 0)
          );

      setAjustesPendientes(ordenAj);

      if (esEdicion && opOriginal) {
        const idsOriginales = new Set(
          (
            opOriginal.aplicacionesAjustes || []
          ).map((ap) =>
            String(
              ap?.ajuste?._id ||
                ap?.ajuste
            )
          )
        );

        setSeleccionAjustes(
          ordenAj.filter((aj) =>
            idsOriginales.has(
              String(aj._id)
            )
          )
        );
      } else {
        setSeleccionAjustes((prev) =>
          prev.filter((x) =>
            ordenAj.some(
              (aj) =>
                String(aj._id) ===
                String(x._id)
            )
          )
        );
      }
    } catch (err) {
      console.error(
        "Error cargando ajustes pendientes proveedor",
        err
      );

      setAjustesPendientes([]);
      setSeleccionAjustes([]);
    }
      try {
        const s = await getSaldosTesoreria();

        if (s?.ok) {
          setCajas(
            Array.isArray(s.cajas) ? s.cajas : []
          );

          setCuentas(
            Array.isArray(s.cuentas) ? s.cuentas : []
          );
        }
      } catch (err) {
        console.error(
          "Error cargando saldos tesorería",
          err
        );
      }

      const uniqById = (arr = []) => {
        const m = new Map(
          arr.map((x) => [String(x._id), x])
        );

        return Array.from(m.values());
      };

      try {
        const normalizarItems = (res) => {
          if (Array.isArray(res)) return res;
          if (Array.isArray(res?.items)) return res.items;
          if (Array.isArray(res?.cartera)) return res.cartera;
          if (Array.isArray(res?.cheques)) return res.cheques;

          if (Array.isArray(res?.data?.items)) {
            return res.data.items;
          }

          if (Array.isArray(res?.data?.cartera)) {
            return res.data.cartera;
          }

          if (Array.isArray(res?.data?.cheques)) {
            return res.data.cheques;
          }

          if (Array.isArray(res?.data)) return res.data;

          return [];
        };

        const normalizarTexto = (v = "") =>
          String(v ?? "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[.,]/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .toUpperCase();

        const palabrasProveedor = (v = "") =>
          normalizarTexto(v)
            .split(" ")
            .filter(
              (p) =>
                p.length > 1 &&
                ![
                  "SA",
                  "SRL",
                  "SAS",
                  "S",
                  "A",
                  "DE",
                  "DEL",
                  "LA",
                  "EL",
                ].includes(p)
            );

        const idDe = (v) => {
          if (!v) return "";
          if (typeof v === "string") return String(v);

          return String(v._id || v.id || "");
        };

        const textoDe = (v) => {
          if (!v) return "";
          if (typeof v === "string") return v;

          return (
            v.razonsocial ||
            v.nombre ||
            v.descripcion ||
            ""
          );
        };

        const proveedorIdCartera = String(
          proveedorActivo?._id ||
            proveedorActivo ||
            ""
        );

        const proveedorNombreOriginal =
          proveedorActivo?.razonsocial ||
          proveedorActivo?.nombre ||
          "";

        const proveedorTokens = palabrasProveedor(
          proveedorNombreOriginal
        );

        const textoDestinoCheque = (x) =>
          [
            textoDe(x.proveedor),
            textoDe(x.proveedorId),
            textoDe(x.beneficiario),
            textoDe(x.destinatario),
            x.proveedorNombre,
            x.beneficiario,
            x.aLaOrdenDe,
            x.pagadoA,
            x.emitidoA,
            x.pagadoAProveedor,
          ]
            .filter(Boolean)
            .join(" ");

        const esParaEsteProveedor = (x) => {
          const idsPosibles = [
            idDe(x.proveedor),
            idDe(x.proveedorId),
            idDe(x.beneficiario),
            idDe(x.destinatario),
            idDe(x.pagadoAProveedor),
          ].filter(Boolean);

          if (
            proveedorIdCartera &&
            idsPosibles.some(
              (id) => String(id) === proveedorIdCartera
            )
          ) {
            return true;
          }

          const destinoTokens = palabrasProveedor(
            textoDestinoCheque(x)
          );

          const comunes = destinoTokens.filter(
            (t) => proveedorTokens.includes(t)
          );

          const minimo = Math.min(
            2,
            proveedorTokens.length,
            destinoTokens.length
          );

          return minimo > 0 && comunes.length >= minimo;
        };

        const resCartera = await getCarteraDisponibles({
          estados: ["EN_CARTERA"],
          estado: "EN_CARTERA",
          desde: 0,
          limit: 5000,
          limite: 5000,
        });

        const itemsCartera = normalizarItems(resCartera);

        const carteraCheq = itemsCartera.filter(
          (x) => x.tipo === "CHEQUE"
        );

        const carteraIC = itemsCartera.filter(
          (x) => x.tipo === "ICHEQ"
        );

        const resEmitidos = await getCartera({
          estados: ["EMITIDO"],
          estado: "EMITIDO",
          desde: 0,
          limit: 5000,
          limite: 5000,
        });

        const itemsEmitidosRaw =
          normalizarItems(resEmitidos);

        // En edición incorporamos también los valores
        // que están actualmente tomados por esta misma OP.
        let itemsOpActual = [];

        if (esEdicion && opId) {
          const [
            resOpActual,
            resOpLegacy,
          ] = await Promise.all([
            getCartera({
              estado: "EMITIDO_A_INGRESAR",
              desde: 0,
              limit: 5000,
              limite: 5000,
            }),

            getCartera({
              estado: "ENDOSADO_A_INGRESAR",
              desde: 0,
              limit: 5000,
              limite: 5000,
            }),
          ]);

          itemsOpActual = uniqById([
            ...normalizarItems(resOpActual),
            ...normalizarItems(resOpLegacy),
          ]).filter((x) => {
            const idOp =
              x?.opId?._id ||
              x?.opId ||
              "";

            return (
              String(idOp) ===
              String(opId)
            );
          });
        }

        const itemsEmitidos = itemsEmitidosRaw.filter(
          (x) => {
            const estado = String(
              x.estado || x.estadoCheque || ""
            ).toUpperCase();

            return (
              (estado === "EMITIDO" || !estado) &&
              esParaEsteProveedor(x)
            );
          }
        );

        const emitCheq = itemsEmitidos.filter(
          (x) => x.tipo === "CHEQUE"
        );

        const emitIC = itemsEmitidos.filter(
          (x) => x.tipo === "ICHEQ"
        );

        const opCheq = itemsOpActual.filter(
          (x) => x.tipo === "CHEQUE"
        );

        const opIC = itemsOpActual.filter(
          (x) => x.tipo === "ICHEQ"
        );

        setCarteraCheques(
          uniqById([
            ...carteraCheq,
            ...emitCheq,
            ...opCheq,
          ])
        );

        setCarteraICheqs(
          uniqById([
            ...carteraIC,
            ...emitIC,
            ...opIC,
          ])
        );

        console.log(
          "Proveedor OP:",
          proveedorNombreOriginal,
          proveedorIdCartera
        );

        console.log("Emitidos RAW OP:", itemsEmitidosRaw);
        console.log("Emitidos filtrados OP:", itemsEmitidos);
      } catch (err) {
        console.error(
          "Error cargando cartera/emitidos",
          err
        );

        setCarteraCheques([]);
        setCarteraICheqs([]);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    esEdicion,
    opId,
    opOriginal?._id,
    proveedorActivo?._id || proveedorActivo,
    clienteActivo?._id || clienteActivo,
    filtrarCliente,
  ]);

  const toggle = (r) => {
    setSeleccion((prev) => {
      const ex = prev.find((x) => x._id === r._id);

      if (ex) {
        return prev.filter((x) => x._id !== r._id);
      }

      const pendA = Number(r.pendienteA ?? 0);
      const pendB = Number(r.pendienteB ?? 0);

      const saldo = Number(
        (pendA + pendB).toFixed(2)
      );

      return saldo > 0.01
        ? [...prev, { ...r, saldo }]
        : prev;
    });
  };

  const tildarTodas = () =>
    setSeleccion(
      pendientes
        .map((r) => {
          const pendA = Number(r.pendienteA ?? 0);
          const pendB = Number(r.pendienteB ?? 0);

          const saldo = Number(
            (pendA + pendB).toFixed(2)
          );

          return { ...r, saldo };
        })
        .filter((r) => r.saldo > 0.01)
    );

  const destildarTodas = () => setSeleccion([]);

  const toggleAjuste = (aj) => {
    setSeleccionAjustes((prev) => {
      const ex = prev.find((x) => x._id === aj._id);

      if (ex) {
        return prev.filter((x) => x._id !== aj._id);
      }

      const saldo = calcularSaldoAjuste(aj);

      return saldo > 0.01
        ? [...prev, { ...aj, saldo }]
        : prev;
    });
  };

  const tildarTodosAjustes = () =>
    setSeleccionAjustes(
      ajustesPendientes
        .map((aj) => ({
          ...aj,
          saldo: calcularSaldoAjuste(aj),
        }))
        .filter((aj) => aj.saldo > 0.01)
    );

  const destildarTodosAjustes = () =>
    setSeleccionAjustes([]);

  const addMedio = (m) =>
    setMedios((p) => [...p, m]);

  const rmMedio = (i) =>
    setMedios((p) =>
      p.filter((_, idx) => idx !== i)
    );

  const addRet = (r) =>
    setRets((p) => [...p, r]);

  const rmRet = (i) =>
    setRets((p) =>
      p.filter((_, idx) => idx !== i)
    );

  const saldoWarnings = useMemo(() => {
    const w = [];

    const idDe = (v) =>
      String(
        v?._id ||
          v ||
          ""
      );

    // ------------------------------------------------------
    // IMPORTES QUE EL BACK VA A REPONER AL EDITAR
    // ------------------------------------------------------

    const reposicionCaja = new Map();
    const reposicionCuenta = new Map();

    if (esEdicion && opOriginal) {
      (opOriginal.pagos || []).forEach((p) => {
        const medio = String(
          p?.medio || ""
        )
          .toLowerCase()
          .trim();

        const imp = Number(
          p?.importe || 0
        );

        if (!(imp > 0)) return;

        if (
          medio === "efectivo" &&
          p?.caja
        ) {
          const id = idDe(p.caja);

          reposicionCaja.set(
            id,
            Number(
              (
                Number(
                  reposicionCaja.get(id) || 0
                ) + imp
              ).toFixed(2)
            )
          );
        }

        if (
          (
            medio === "transferencia" ||
            medio === "deposito"
          ) &&
          p?.cuentaBancaria
        ) {
          const id =
            idDe(p.cuentaBancaria);

          reposicionCuenta.set(
            id,
            Number(
              (
                Number(
                  reposicionCuenta.get(id) || 0
                ) + imp
              ).toFixed(2)
            )
          );
        }
      });
    }

    // ------------------------------------------------------
    // AGRUPAR LOS NUEVOS PAGOS POR CAJA / CUENTA
    // ------------------------------------------------------

    const efectivoPorCaja = new Map();
    const bancoPorCuenta = new Map();

    medios.forEach((m, i) => {
      const imp = Number(
        m.importe || 0
      );

      if (m.medio === "efectivo") {
        if (!m.caja) {
          w.push(
            `Pago #${i + 1}: seleccione una Caja.`
          );
        } else {
          const id =
            String(m.caja);

          efectivoPorCaja.set(
            id,
            Number(
              (
                Number(
                  efectivoPorCaja.get(id) || 0
                ) + imp
              ).toFixed(2)
            )
          );
        }
      }

      if (
        m.medio === "transferencia" ||
        m.medio === "deposito"
      ) {
        if (!m.cuentaBancaria) {
          w.push(
            `Pago #${i + 1}: seleccione una Cuenta.`
          );
        } else {
          const id =
            String(m.cuentaBancaria);

          bancoPorCuenta.set(
            id,
            Number(
              (
                Number(
                  bancoPorCuenta.get(id) || 0
                ) + imp
              ).toFixed(2)
            )
          );
        }
      }

      if (
        m.medio === "cheque" ||
        m.medio === "icheq"
      ) {
        if (!m.carteraId) {
          w.push(
            `Pago #${i + 1}: seleccione un ${m.medio} de cartera.`
          );
        }
      }
    });

    // ------------------------------------------------------
    // VALIDAR EFECTIVO
    // saldo actual + lo que devolverá la OP anterior
    // ------------------------------------------------------

    efectivoPorCaja.forEach(
      (totalNuevo, cajaId) => {
        const cx = cajas.find(
          (c) =>
            String(c._id) ===
            String(cajaId)
        );

        const saldoActual = Number(
          cx?.saldoActual || 0
        );

        const repuesto = Number(
          reposicionCaja.get(
            String(cajaId)
          ) || 0
        );

        const disponible =
          saldoActual + repuesto;

        if (
          totalNuevo >
          disponible + 0.0001
        ) {
          w.push(
            `${
              cx?.descripcion ||
              "Caja"
            }: importe supera saldo de caja (${disponible.toLocaleString(
              "es-AR"
            )}).`
          );
        }
      }
    );

    // ------------------------------------------------------
    // VALIDAR TRANSFERENCIAS / DEPÓSITOS
    // ------------------------------------------------------

    bancoPorCuenta.forEach(
      (totalNuevo, cuentaId) => {
        const ct = cuentas.find(
          (c) =>
            String(c._id) ===
            String(cuentaId)
        );

        const saldoActual = Number(
          ct?.saldoActual || 0
        );

        const repuesto = Number(
          reposicionCuenta.get(
            String(cuentaId)
          ) || 0
        );

        const disponible =
          saldoActual + repuesto;

        const saldoPost =
          disponible - totalNuevo;

        if (
          ct &&
          disponible >= 0 &&
          saldoPost < 0
        ) {
          w.push(
            `${
              ct.banco ||
              ct.descripcion ||
              "Cuenta"
            }: la cuenta quedará en negativo (${saldoPost.toLocaleString(
              "es-AR"
            )}).`
          );
        }
      }
    );

    return w;
  }, [
    medios,
    cajas,
    cuentas,
    esEdicion,
    opOriginal,
  ]);

  const hardErrors = useMemo(() => {
    const re =
      /seleccione una Caja|seleccione una Cuenta|seleccione un (cheque|icheq)|importe supera saldo de caja/i;

    return saldoWarnings.filter((w) => re.test(w));
  }, [saldoWarnings]);

  const softWarnings = useMemo(
    () =>
      saldoWarnings.filter(
        (w) => !hardErrors.includes(w)
      ),
    [saldoWarnings, hardErrors]
  );

  const canSubmit =
    Boolean(
      proveedorActivo?._id ||
        proveedorActivo
    ) &&
    repartoValido &&
    totalPagos > 0 &&
    montoAplicable > 0 &&
    hardErrors.length === 0;

  const guardar = async () => {
    try {
      if (!canSubmit) return;

      if (!hayConceptosPago) {
        alert(
          "Marcá al menos un concepto a pagar: Compra A o Compra B."
        );
        return;
      }

      if (hardErrors.length > 0) {
        alert(
          "Hay errores:\n- " +
          hardErrors.join("\n- ")
        );
        return;
      }

      if (softWarnings.length > 0) {
        const seguir = window.confirm(
          "Hay advertencias:\n- " +
          softWarnings.join("\n- ") +
          "\n\n¿Deseás continuar de todas formas?"
        );

        if (!seguir) return;
      }

      const payload = {
        proveedor:
          proveedorActivo?._id ||
          proveedorActivo,

        cliente: filtrarCliente
          ? clienteActivo?._id ||
            clienteActivo
          : esEdicion
          ? null
          : undefined,

        fecha,
        observaciones: obs,

        modoAplicacion:
          seleccion.length > 0
            ? "antiguedad"
            : "manual",

        conceptos: {
          pagaA,
          pagaB,
        },

        montosPorCuenta,

        remitosIds:
          seleccion.length > 0
            ? seleccion.map((r) => r._id)
            : [],

        aplicaciones:
          seleccion.length > 0 ? undefined : [],

        aplicacionesAjustes:
          aplicacionesAjustesDistribuidas.apps,

        pagos: medios.map((m) => {
          const base = {
            medio: m.medio,
            importe: Number(m.importe || 0),
            referencia:
              m.referencia || m.nroCheque || "",
          };

          if (m.medio === "efectivo") {
            return {
              ...base,
              caja: m.caja,
            };
          }

          if (
            m.medio === "transferencia" ||
            m.medio === "deposito"
          ) {
            return {
              ...base,
              cuentaBancaria: m.cuentaBancaria,
            };
          }

          if (
            m.medio === "cheque" ||
            m.medio === "icheq"
          ) {
            return {
              ...base,
              carteraId: m.carteraId,
            };
          }

          return base;
        }),

        retenciones: rets.map((r) => ({
          tipo: (r.tipo || "IIBB").toUpperCase(),
          base: Number(r.base || 0),
          porcentaje: Number(r.porcentaje || 0),
          importe: Number(r.importe || 0),
          comprobante: r.comprobante || "",
        })),
      };

      const res = esEdicion
        ? await editarOrdenPagoRemito(
            opId,
            payload
          )
        : await postOrdenPagoRemito(
            payload
          );

      if (res?.ok) {
        const orden = res?.orden || {};
        const numeros = [];

        if (orden.numeroA) {
          numeros.push(`Cuenta A: ${orden.numeroA}`);
        }

        if (orden.numeroB) {
          numeros.push(`Cuenta B: ${orden.numeroB}`);
        }

        if (
          !orden.numeroA &&
          !orden.numeroB &&
          orden.numero
        ) {
          numeros.push(`Orden: ${orden.numero}`);
        }

        const detalle =
          numeros.length > 0
            ? `\n\n${numeros.join("\n")}`
            : "";

        alert(
          `Orden de pago guardada correctamente.${detalle}`
        );

        onHide(true);
      } else {
        alert(res?.msg || "Error al guardar.");
      }
    } catch (e) {
      console.error(
        "OP Remitos error:",
        e?.response?.data || e
      );

      alert(
        e?.response?.data?.msg ||
        e.message ||
        "Error al guardar."
      );
    }
  };

  const updRet = (idx, patch) => {
    setRets((p) =>
      p.map((x, i) =>
        i === idx ? { ...x, ...patch } : x
      )
    );
  };

  const retKey = (idx, campo) =>
    `${idx}-${campo}`;

  const retMoneyValue = (idx, r, campo) => {
    const key = retKey(idx, campo);

    if (retEditando === key) {
      return retTexto[key] ?? "";
    }

    const valor = Number(r?.[campo] || 0);

    return valor ? moneyInput(valor) : "";
  };

  const retDecimalValue = (idx, r, campo) => {
    const key = retKey(idx, campo);

    if (retEditando === key) {
      return retTexto[key] ?? "";
    }

    const valor = Number(r?.[campo] || 0);

    return valor
      ? String(valor).replace(".", ",")
      : "";
  };

  const onFocusRet = (idx, campo, valor) => {
    const key = retKey(idx, campo);

    setRetEditando(key);

    setRetTexto((prev) => ({
      ...prev,
      [key]: valor
        ? String(valor).replace(".", ",")
        : "",
    }));
  };

  const onBlurRet = (idx, campo) => {
    const key = retKey(idx, campo);

    setRetEditando(null);

    setRetTexto((prev) => {
      const nuevo = { ...prev };
      delete nuevo[key];
      return nuevo;
    });
  };

  const nombreProv = (
    proveedorActivo?.razonsocial ||
    proveedorActivo?.nombre ||
    ""
  ).toString();

  const nombreCli = (
    clienteActivo?.razonsocial ||
    clienteActivo?.nombre ||
    ""
  ).toString();

  return (
    <Modal
      show={show}
      onHide={() => onHide(false)}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {esEdicion
            ? "Editar Orden de Pago (Remitos)"
            : "Nueva Orden de Pago (Remitos)"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-2">
          <Col md={4}>
            <Form.Label>Proveedor</Form.Label>
            <Form.Control
              readOnly
              value={nombreProv}
            />
          </Col>

          <Col md={4}>
            <Form.Label>
              Cliente (filtro opcional)
            </Form.Label>

            <Form.Control
              readOnly
              value={nombreCli}
              placeholder="(sin filtro)"
            />

            <div className="mt-1">
              <Form.Check
                type="switch"
                id="filtrar-por-cliente"
                label="Filtrar por este cliente"
                checked={filtrarCliente}
                onChange={(e) =>
                  setFiltrarCliente(e.target.checked)
                }
              />
            </div>
          </Col>

          <Col md={4}>
            <Form.Label>Fecha</Form.Label>
            <Form.Control
              type="date"
              value={fecha}
              onChange={(e) =>
                setFecha(e.target.value)
              }
            />
          </Col>
        </Row>

        <Row className="align-items-center mb-2">
          <Col>
            <b>Remitos pendientes del proveedor</b>
          </Col>

          <Col className="text-end">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={tildarTodas}
            >
              Tildar todos
            </Button>{" "}

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={destildarTodas}
            >
              Destildar todos
            </Button>{" "}

            <Button
              size="sm"
              variant="outline-primary"
              onClick={handlePdfPendientes}
            >
              PDF pendientes
            </Button>{" "}

            {Boolean(seleccion.length) && (
              <span className="ms-2">
                Seleccionados: <b>{seleccion.length}</b>
                {" "}— Σ: <b>${$(totalSeleccion)}</b>
              </span>
            )}
          </Col>
        </Row>

        <div
          style={{
            maxHeight: 260,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
        >
          <Table
            size="sm"
            bordered
            hover
            className="m-0"
          >
            <thead>
              <tr
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#f8f9fa",
                  zIndex: 2,
                }}
              >
                <th style={{ width: 40 }}></th>
                <th>Remito</th>
                <th>Fecha</th>
                <th style={{ textAlign: "right" }}>
                  Pendiente Compra A (blanco + IVA)
                </th>
                <th style={{ textAlign: "right" }}>
                  Pendiente Compra B (negro)
                </th>
                <th style={{ textAlign: "right" }}>
                  Saldo
                </th>
              </tr>
            </thead>

            <tbody>
              {pendientes.map((r) => {
                const checked = !!seleccion.find(
                  (x) => x._id === r._id
                );

                const pendA = Number(
                  r.pendienteA ??
                    Math.max(
                      0,
                      Number(r.totalCompraA || 0) -
                        Number(r.pagadoA || 0)
                    )
                );

                const pendB = Number(
                  r.pendienteB ??
                    Math.max(
                      0,
                      Number(r.totalCompraB || 0) -
                        Number(r.pagadoB || 0)
                    )
                );

                const saldo = Number(
                  (pendA + pendB).toFixed(2)
                );

                const disabled = saldo <= 0.01;
                const id = `sel-r-${r._id}`;

                return (
                  <tr
                    key={r._id}
                    style={
                      disabled
                        ? { opacity: 0.5 }
                        : undefined
                    }
                  >
                    <td
                      style={{
                        width: 40,
                        textAlign: "center",
                      }}
                    >
                      <input
                        id={id}
                        type="checkbox"
                        className="form-check-input m-0 p-0"
                        disabled={disabled}
                        checked={checked}
                        onChange={() => toggle(r)}
                        onClick={(e) =>
                          e.stopPropagation()
                        }
                      />
                    </td>

                    <td>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        #{String(r.codrem).padStart(6, "0")}
                      </label>
                    </td>

                    <td style={{ textAlign: "center" }}>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        {fmtFechaDoc(r.fecha)}
                      </label>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        ${$(pendA)}
                      </label>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        ${$(pendB)}
                      </label>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        ${$(saldo)}
                      </label>
                    </td>
                  </tr>
                );
              })}

              {!pendientes.length && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center text-muted"
                  >
                    Sin pendientes para este proveedor/cliente
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div
          className="mt-2 p-2 rounded"
          style={{
            backgroundColor: "#e3f2fd",
            border: "1px solid #90caf9",
            color: "#0d47a1",
            fontWeight: "bold",
          }}
        >
          <Row>
            <Col md={4}>
              Σ Pendiente A (blanco + IVA): $
              {$(totalesPendientes.compraA)}
            </Col>

            <Col md={4}>
              Σ Pendiente B (negro): $
              {$(totalesPendientes.compraB)}
            </Col>

            <Col md={4}>
              Σ Saldo pendiente total: $
              {$(totalesPendientes.saldo)}
            </Col>
          </Row>
        </div>

        <Row className="align-items-center mt-3 mb-2">
          <Col>
            <b>Ajustes pendientes del proveedor</b>
          </Col>

          <Col className="text-end">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={tildarTodosAjustes}
            >
              Tildar ajustes
            </Button>{" "}

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={destildarTodosAjustes}
            >
              Destildar ajustes
            </Button>{" "}

            {Boolean(seleccionAjustes.length) && (
              <span className="ms-2">
                Ajustes seleccionados:{" "}
                <b>{seleccionAjustes.length}</b>
                {" "}— Σ:{" "}
                <b>${$(totalSeleccionAjustes)}</b>
              </span>
            )}
          </Col>
        </Row>

        <div
          style={{
            maxHeight: 180,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
        >
          <Table
            size="sm"
            bordered
            hover
            className="m-0"
          >
            <thead>
              <tr
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#f8f9fa",
                  zIndex: 2,
                }}
              >
                <th style={{ width: 40 }}></th>
                <th>Número</th>
                <th>Fecha</th>
                <th>Concepto</th>
                <th style={{ textAlign: "right" }}>
                  Saldo A
                </th>
                <th style={{ textAlign: "right" }}>
                  Saldo B
                </th>
                <th style={{ textAlign: "right" }}>
                  Saldo
                </th>
              </tr>
            </thead>

            <tbody>
              {ajustesPendientes.map((aj) => {
                const checked =
                  !!seleccionAjustes.find(
                    (x) => x._id === aj._id
                  );

                const saldoA = Number(aj.saldoA || 0);
                const saldoB = Number(aj.saldoB || 0);
                const saldo = calcularSaldoAjuste(aj);

                const disabled = saldo <= 0.01;
                const id = `sel-aj-${aj._id}`;

                return (
                  <tr
                    key={aj._id}
                    style={
                      disabled
                        ? { opacity: 0.5 }
                        : undefined
                    }
                  >
                    <td
                      style={{
                        width: 40,
                        textAlign: "center",
                      }}
                    >
                      <input
                        id={id}
                        type="checkbox"
                        className="form-check-input m-0 p-0"
                        disabled={disabled}
                        checked={checked}
                        onChange={() => toggleAjuste(aj)}
                        onClick={(e) =>
                          e.stopPropagation()
                        }
                      />
                    </td>

                    <td>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        {aj.numero || "-"}
                      </label>
                    </td>

                    <td style={{ textAlign: "center" }}>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        {fmtFechaDoc(aj.fecha)}
                      </label>
                    </td>

                    <td>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        {aj.concepto || aj.observaciones || "-"}
                      </label>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        ${$(saldoA)}
                      </label>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        ${$(saldoB)}
                      </label>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <label
                        htmlFor={id}
                        className="w-100 m-0"
                      >
                        ${$(saldo)}
                      </label>
                    </td>
                  </tr>
                );
              })}

              {!ajustesPendientes.length && (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center text-muted"
                  >
                    Sin ajustes pendientes para este proveedor
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <Row className="mt-2">
          <Col>
            <Form.Check
              inline
              type="checkbox"
              id="paga-a"
              label="Pagar Compra A (blanco)"
              checked={pagaA}
              onChange={(e) =>
                setPagaA(e.target.checked)
              }
            />

            <Form.Check
              inline
              type="checkbox"
              id="paga-b"
              label="Pagar Compra B (negro)"
              checked={pagaB}
              onChange={(e) =>
                setPagaB(e.target.checked)
              }
            />
          </Col>
        </Row>

        {hayConceptosPago && (
          <Row className="mt-2">
            {cuentasActivas.map((cuenta) => (
              <Col md={4} key={cuenta}>
                <Form.Label>
                  Importe destinado a {cuenta}
                </Form.Label>

                <Form.Control
                  type="text"
                  inputMode="decimal"
                  placeholder="Ej.: 100.000,50"
                  readOnly={cuentasActivas.length === 1}
                  value={
                    cuentasActivas.length === 1
                      ? moneyInput(montoAplicable)
                      : importesCuenta[cuenta]
                  }
                  onChange={(e) =>
                    setImportesCuenta((prev) => ({
                      ...prev,
                      [cuenta]: e.target.value,
                    }))
                  }
                />
              </Col>
            ))}

            <Col md={12} className="mt-1">
              <small
                className={
                  repartoValido
                    ? "text-success"
                    : "text-danger"
                }
              >
                A + B debe sumar pagos + retenciones:
                {" "}${$(montoAplicable)}.
                {cuentasActivas.length === 2 &&
                  " Indicá el importe de cada cuenta; usá coma para los decimales."}
              </small>
            </Col>
          </Row>
        )}

        <Row className="mt-3">
          <Col md={6}>
            <h6>Pagos</h6>

            <Button
              size="sm"
              onClick={() =>
                addMedio({
                  medio: "transferencia",
                  importe: 0,
                  cuentaBancaria: "",
                })
              }
            >
              + Transferencia
            </Button>{" "}

            <Button
              size="sm"
              onClick={() =>
                addMedio({
                  medio: "deposito",
                  importe: 0,
                  cuentaBancaria: "",
                })
              }
            >
              + Depósito
            </Button>{" "}

            <Button
              size="sm"
              onClick={() =>
                addMedio({
                  medio: "cheque",
                  importe: 0,
                  carteraId: "",
                  referencia: "",
                })
              }
            >
              + Cheque
            </Button>{" "}

            <Button
              size="sm"
              onClick={() =>
                addMedio({
                  medio: "icheq",
                  importe: 0,
                  carteraId: "",
                  referencia: "",
                })
              }
            >
              + iCheq
            </Button>{" "}

            <Button
              size="sm"
              onClick={() =>
                addMedio({
                  medio: "efectivo",
                  importe: 0,
                  caja: "",
                })
              }
            >
              + Efectivo
            </Button>

            <Table size="sm" className="mt-2">
              <thead>
                <tr>
                  <th>Medio</th>
                  <th>Origen / Datos</th>
                  <th>Importe</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {medios.map((m, i) => {
                  const selCaja = cajas.find(
                    (c) =>
                      String(c._id) === String(m.caja)
                  );

                  const selCta = cuentas.find(
                    (c) =>
                      String(c._id) ===
                      String(m.cuentaBancaria)
                  );

                  const listaCartera =
                    m.medio === "cheque"
                      ? carteraCheques
                      : carteraICheqs;

                  return (
                    <tr key={i}>
                      <td style={{ width: 120 }}>
                        <Form.Select
                          size="sm"
                          value={m.medio}
                          onChange={(e) =>
                            setMedios((p) =>
                              p.map((x, idx) =>
                                idx === i
                                  ? {
                                      ...x,
                                      medio: e.target.value,
                                      caja: "",
                                      cuentaBancaria: "",
                                      carteraId: "",
                                      referencia: "",
                                      importe: 0,
                                    }
                                  : x
                              )
                            )
                          }
                        >
                          <option value="transferencia">
                            Transferencia
                          </option>
                          <option value="deposito">
                            Depósito
                          </option>
                          <option value="cheque">
                            Cheque
                          </option>
                          <option value="icheq">
                            iCheq
                          </option>
                          <option value="efectivo">
                            Efectivo
                          </option>
                        </Form.Select>
                      </td>

                      <td>
                        {m.medio === "efectivo" && (
                          <div className="d-flex gap-2">
                            <Form.Select
                              size="sm"
                              value={m.caja || ""}
                              style={{ minWidth: 260 }}
                              onChange={(e) =>
                                setMedios((p) =>
                                  p.map((x, idx) =>
                                    idx === i
                                      ? {
                                          ...x,
                                          caja: e.target.value,
                                        }
                                      : x
                                  )
                                )
                              }
                            >
                              <option value="">
                                -- Caja --
                              </option>

                              {cajas.map((c) => (
                                <option
                                  key={c._id}
                                  value={c._id}
                                >
                                  {c.descripcion} — Saldo:
                                  {" "}${$(c.saldoActual || 0)}
                                </option>
                              ))}
                            </Form.Select>

                            {selCaja && (
                              <span className="text-muted">
                                Saldo:{" "}
                                ${$(selCaja.saldoActual || 0)}
                              </span>
                            )}
                          </div>
                        )}

                        {(m.medio === "transferencia" ||
                          m.medio === "deposito") && (
                          <div className="d-flex gap-2">
                            <Form.Select
                              size="sm"
                              value={m.cuentaBancaria || ""}
                              style={{ minWidth: 260 }}
                              onChange={(e) =>
                                setMedios((p) =>
                                  p.map((x, idx) =>
                                    idx === i
                                      ? {
                                          ...x,
                                          cuentaBancaria:
                                            e.target.value,
                                        }
                                      : x
                                  )
                                )
                              }
                            >
                              <option value="">
                                -- Cuenta --
                              </option>

                              {cuentas.map((c) => (
                                <option
                                  key={c._id}
                                  value={c._id}
                                >
                                  {c.banco ||
                                    c.descripcion ||
                                    c.alias ||
                                    c.numero ||
                                    "Cuenta"}
                                  {" "}— Saldo:{" "}
                                  ${$(c.saldoActual || 0)}
                                </option>
                              ))}
                            </Form.Select>

                            {selCta && (
                              <span className="text-muted">
                                Saldo:{" "}
                                ${$(selCta.saldoActual || 0)}
                              </span>
                            )}
                          </div>
                        )}

                        {(m.medio === "cheque" ||
                          m.medio === "icheq") && (
                          <div className="d-flex flex-column gap-1">
                            <Form.Select
                              size="sm"
                              value={m.carteraId || ""}
                              style={{ minWidth: 260 }}
                              onChange={(e) => {
                                const id = e.target.value;

                                setMedios((p) =>
                                  p.map((x, idx) => {
                                    if (idx !== i) return x;

                                    const item = listaCartera.find(
                                      (ch) =>
                                        String(ch._id) === String(id)
                                    );

                                    if (!item) {
                                      return {
                                        ...x,
                                        carteraId: "",
                                        importe: 0,
                                        referencia: "",
                                      };
                                    }

                                    const ref =
                                      item.nro ||
                                      item.nroCheque ||
                                      item.idICheq ||
                                      item.referencia ||
                                      "";

                                    return {
                                      ...x,
                                      carteraId: id,
                                      importe: item.importe || 0,
                                      referencia: ref,
                                    };
                                  })
                                );
                              }}
                            >
                              <option value="">
                                --{" "}
                                {m.medio === "cheque"
                                  ? "Cheque"
                                  : "iCheq"}
                                {" "}disponible --
                              </option>

                              {listaCartera.map((ch) => (
                                <option
                                  key={ch._id}
                                  value={ch._id}
                                >
                                  {m.medio === "cheque"
                                    ? `Ch ${
                                        ch.nro || ch.nroCheque || ""
                                      } — $${$(ch.importe || 0)} — ${
                                        ch.bancoEmisor || ""
                                      }`
                                    : `iCheq ${
                                        ch.nro || ch.idICheq || ""
                                      } — $${$(ch.importe || 0)}`}
                                </option>
                              ))}
                            </Form.Select>

                            {m.referencia && (
                              <span className="text-muted">
                                Ref: {m.referencia}
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      <td style={{ width: 190 }}>
                        <InputGroup size="sm">
                          <InputGroup.Text>
                            $
                          </InputGroup.Text>

                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            className="text-end"
                            value={
                              importeEditando === i
                                ? importeTexto[i] ??
                                  String(m.importe || "")
                                : moneyInput(m.importe)
                            }
                            readOnly={
                              m.medio === "cheque" ||
                              m.medio === "icheq"
                            }
                            onFocus={() => {
                              setImporteEditando(i);

                              setImporteTexto((prev) => ({
                                ...prev,
                                [i]: m.importe
                                  ? String(m.importe).replace(".", ",")
                                  : "",
                              }));
                            }}
                            onChange={(e) => {
                              const texto = e.target.value;

                              setImporteTexto((prev) => ({
                                ...prev,
                                [i]: texto,
                              }));

                              const importe =
                                parseMoneyInput(texto);

                              setMedios((p) =>
                                p.map((x, idx) =>
                                  idx === i
                                    ? {
                                        ...x,
                                        importe: Number.isFinite(importe)
                                          ? importe
                                          : 0,
                                      }
                                    : x
                                )
                              );
                            }}
                            onBlur={() => {
                              setImporteEditando(null);

                              setImporteTexto((prev) => {
                                const nuevo = { ...prev };
                                delete nuevo[i];
                                return nuevo;
                              });
                            }}
                          />
                        </InputGroup>
                      </td>

                      <td style={{ width: 50 }}>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => rmMedio(i)}
                        >
                          x
                        </Button>
                      </td>
                    </tr>
                  );
                })}

                {!medios.length && (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-muted"
                    >
                      Agregá al menos un pago
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {saldoWarnings.length > 0 && (
              <ul
                className="mt-2"
                style={{ color: "crimson" }}
              >
                {saldoWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </Col>

          <Col md={6}>
            <h6>Retenciones</h6>

            <Button
              size="sm"
              onClick={() =>
                addRet({
                  tipo: "IIBB",
                  base: "",
                  porcentaje: "",
                  importe: "",
                })
              }
            >
              + IIBB
            </Button>{" "}

            <Button
              size="sm"
              onClick={() =>
                addRet({
                  tipo: "GAN",
                  base: "",
                  porcentaje: "",
                  importe: "",
                })
              }
            >
              + Ganancias
            </Button>{" "}

            <Button
              size="sm"
              onClick={() =>
                addRet({
                  tipo: "IVA",
                  base: "",
                  porcentaje: "",
                  importe: "",
                })
              }
            >
              + IVA
            </Button>{" "}

            <Button
              size="sm"
              onClick={() =>
                addRet({
                  tipo: "OTRA",
                  base: "",
                  porcentaje: "",
                  importe: "",
                })
              }
            >
              + Otra
            </Button>

            <Table size="sm" className="mt-2">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Base</th>
                  <th>%</th>
                  <th>Importe</th>
                  <th>Comp.</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {rets.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={r.tipo}
                        onChange={(e) =>
                          setRets((p) =>
                            p.map((x, idx) =>
                              idx === i
                                ? {
                                    ...x,
                                    tipo: e.target.value,
                                  }
                                : x
                            )
                          )
                        }
                      >
                        <option value="IIBB">
                          IIBB
                        </option>
                        <option value="GAN">
                          Ganancias
                        </option>
                        <option value="IVA">
                          IVA
                        </option>
                        <option value="OTRA">
                          OTRA
                        </option>
                      </Form.Select>
                    </td>

                    <td style={{ width: 110 }}>
                      <Form.Control
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        className="text-end"
                        placeholder="0,00"
                        value={retMoneyValue(i, r, "base")}
                        onFocus={() =>
                          onFocusRet(i, "base", r.base)
                        }
                        onChange={(e) => {
                          const texto = limpiarTextoRetencion(
                            e.target.value
                          );

                          setRetTexto((prev) => ({
                            ...prev,
                            [retKey(i, "base")]: texto,
                          }));

                          const base = parseRetencionInput(texto);

                          updRet(i, {
                            base: Number.isFinite(base) ? base : 0,
                          });
                        }}
                        onBlur={() =>
                          onBlurRet(i, "base")
                        }
                      />
                    </td>

                    <td style={{ width: 90 }}>
                      <Form.Control
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        className="text-end"
                        placeholder="0,00"
                        value={retDecimalValue(i, r, "porcentaje")}
                        onFocus={() =>
                          onFocusRet(
                            i,
                            "porcentaje",
                            r.porcentaje
                          )
                        }
                        onChange={(e) => {
                          const texto = limpiarTextoRetencion(
                            e.target.value
                          );

                          setRetTexto((prev) => ({
                            ...prev,
                            [retKey(i, "porcentaje")]: texto,
                          }));

                          const porcentaje =
                            parseRetencionInput(texto);

                          updRet(i, {
                            porcentaje: Number.isFinite(porcentaje)
                              ? porcentaje
                              : 0,
                          });
                        }}
                        onBlur={() =>
                          onBlurRet(i, "porcentaje")
                        }
                      />
                    </td>

                    <td style={{ width: 120 }}>
                      <Form.Control
                        size="sm"
                        type="text"
                        inputMode="decimal"
                        className="text-end"
                        placeholder="0,00"
                        value={retMoneyValue(i, r, "importe")}
                        onFocus={() =>
                          onFocusRet(i, "importe", r.importe)
                        }
                        onChange={(e) => {
                          const texto = limpiarTextoRetencion(
                            e.target.value
                          );

                          setRetTexto((prev) => ({
                            ...prev,
                            [retKey(i, "importe")]: texto,
                          }));

                          const importe =
                            parseRetencionInput(texto);

                          updRet(i, {
                            importe: Number.isFinite(importe)
                              ? importe
                              : 0,
                          });
                        }}
                        onBlur={() =>
                          onBlurRet(i, "importe")
                        }
                      />
                    </td>

                    <td style={{ width: 140 }}>
                      <Form.Control
                        size="sm"
                        placeholder="Comprobante"
                        value={r.comprobante || ""}
                        onChange={(e) =>
                          setRets((p) =>
                            p.map((x, idx) =>
                              idx === i
                                ? {
                                    ...x,
                                    comprobante: e.target.value,
                                  }
                                : x
                            )
                          )
                        }
                      />
                    </td>

                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => rmRet(i)}
                      >
                        x
                      </Button>
                    </td>
                  </tr>
                ))}

                {!rets.length && (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-muted"
                    >
                      Agregá retenciones si corresponde
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Form.Label>Observaciones</Form.Label>

            <Form.Control
              as="textarea"
              rows={2}
              value={obs}
              onChange={(e) =>
                setObs(e.target.value)
              }
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={3}>
            <b>Σ Selección:</b>{" "}
            ${$(totalSeleccionGeneral)}
          </Col>

          <Col md={3}>
            <b>Σ Pagos:</b>{" "}
            ${$(totalPagos)}
          </Col>

          <Col md={3}>
            <b>Σ Retenciones:</b>{" "}
            ${$(totalRets)}
          </Col>

          <Col md={3}>
            <b>Σ Aplicable:</b>{" "}
            ${$(montoAplicable)}
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <div className="text-muted">
              Diferencia (Selección − Aplicable):{" "}
              <b
                style={{
                  color:
                    Math.abs(Number(diferenciaVisual)) <= 0.01
                      ? "green"
                      : "crimson",
                }}
              >
                {$(diferenciaVisual)}
              </b>{" "}
              (informativa)
            </div>

            {saldoFavorGenerado > 0 && (
              <div className="text-primary mt-1">
                Saldo a favor:
                {" "}A ${$(aplicacionesAjustesDistribuidas.restantes.A)}
                {" · "}B ${$(aplicacionesAjustesDistribuidas.restantes.B)}.
                {" "}Total: ${$(saldoFavorGenerado)}.
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => onHide(false)}
        >
          Cancelar
        </Button>

        <Button
          variant="primary"
          onClick={guardar}
          disabled={!canSubmit}
        >
          Guardar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}